import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _some from 'lodash/some';
import html2pdf from 'html2pdf.js';
import { convertToPattern } from 'containers/Assessment/utils';

const MAX_VALUE = 100;

export const normalizeResult = (
  value,
  minimumScore,
  maximumScore,
  interval,
  scale,
  reverse = false,
) => {
  const maxValue = scale || MAX_VALUE;
  const normalize = x =>
    ((x - minimumScore) * maxValue) / (maximumScore - minimumScore);
  const normalizedValue = Math.round(normalize(value));
  const normalizedIntervalStart = normalize((interval && interval.start) || 0);
  const finalIntervalStart =
    normalizedIntervalStart < 0 ? 0 : normalizedIntervalStart;
  const normalizedIntervalEnd = normalize(
    (interval && interval.end) || maxValue,
  );
  const finalIntervalEnd =
    normalizedIntervalEnd > maxValue ? maxValue : normalizedIntervalEnd;

  return {
    normalizedValue: reverse ? maxValue - normalizedValue : normalizedValue,
    normalizedIntervalStart: reverse
      ? maxValue - finalIntervalEnd
      : finalIntervalStart,
    normalizedIntervalEnd: reverse
      ? maxValue - finalIntervalStart
      : finalIntervalEnd,
    normalizedMaxValue: maxValue,
  };
};

export const normalizeIntervals = (
  intervals,
  minimumScore,
  maximumScore,
  scale,
  reverse = false,
) => {
  const maxValue = scale || MAX_VALUE;
  const normalize = x =>
    ((x - minimumScore) * maxValue) / (maximumScore - minimumScore);

  const sortedIntervals = reverse ? intervals.reverse() : intervals;
  const normalizedIntervals = sortedIntervals.map(interval => {
    const normalizedStart = normalize(
      reverse
        ? maxValue - (_get(interval, 'end') || maxValue)
        : _get(interval, 'start') || 0,
    );
    const normalizedEnd = normalize(
      reverse
        ? maxValue - (_get(interval, 'start') || 0)
        : _get(interval, 'end') || maxValue,
    );

    return {
      ...interval,
      start: normalizedStart < 0 ? 0 : normalizedStart,
      end: normalizedEnd > maxValue ? maxValue : normalizedEnd,
    };
  });

  return normalizedIntervals;
};

export const getCurrentInterval = (intervals, score) => {
  if (!intervals) return [];

  const sortedIntervals = intervals.sort((a, b) => a.end - b.end);
  let currentInterval;
  let i = 0;
  while (!currentInterval && i < sortedIntervals.length) {
    if (score <= sortedIntervals[i].end)
      currentInterval = [sortedIntervals[i], i];

    i += 1;
  }

  if (!currentInterval) return [sortedIntervals[0], 0];

  return currentInterval;
};

export const getThermometerInterval = (value, scale) => ({
  start: value,
  end: scale || MAX_VALUE,
  color: '#CCCCCC',
});

export function sortAscending(a, b) {
  return a.scoring - b.scoring;
}

export function sortDescending(a, b) {
  return b.scoring - a.scoring;
}

export function sortBySummary(mapping) {
  return (a, b) => (mapping[a.summaryId] || 0) - (mapping[b.summaryId] || 0);
}

export function filterByTagAndClient({ list, profile, clientDetails }) {
  let profileTag = _get(profile, 'insurance', []);
  if (typeof profileTag === 'string') {
    profileTag = [profileTag];
  }
  const patternProfileTag = convertToPattern('insurance', profileTag);

  const isClient = tags =>
    tags.filter(
      tag =>
        _isEmpty(_get(tag, 'fields.client')) ||
        _get(tag, 'fields.client', []).filter(
          clt => _get(clt, 'sys.id', null) === _get(clientDetails, 'sys.id'),
        ).length > 0,
    ).length > 0;

  const finalList = list.filter(lt =>
    _get(lt, 'fields.tags', []).filter(tag =>
      _some(patternProfileTag, item =>
        item
          .toLowerCase()
          .includes(_get(tag, 'fields.title', '').toLowerCase()),
      ),
    ).length > 0
      ? isClient(_get(lt, 'fields.tags', []))
      : true,
  );

  return finalList;
}

export function filterByAudienceType({ list, profile }) {
  const ageGroupfn = dob => {
    switch (dob) {
      case '[<18]':
        return 'Teens';
      case '[18-24]':
        return 'Young Adults';
      case '[25-34]':
        return '+Young Adults';
      case '[35-44]':
        return '+Young Adults';
      case '[45-54]':
        return '+Young Adults';
      case '[55-64]':
        return '+Young Adults';
      case '[>65]':
        return 'Seniors';
      default:
        return dob;
    }
  };

  const profileDOB = _get(profile, 'dob', '');
  const ageGroup = ageGroupfn(profileDOB);

  const finalList = list.filter(
    lt =>
      _isEmpty(_get(lt, 'fields.audienceType')) ||
      ageGroup === '+Young Adults' ||
      !profileDOB ||
      _get(lt, 'fields.audienceType', []).filter(audience =>
        _get(audience, 'fields.name', '')
          .toLowerCase()
          .includes(ageGroup.toLowerCase()),
      ).length > 0,
  );

  return finalList;
}

export const getAnswersMatchedFilters = (
  type,
  config,
  answers,
  overallScoring,
  scorings = [],
  locale,
) => {
  const filters = [];
  Object.keys(_get(config, type, {})).forEach(key => {
    if (key === 'scorings') {
      Object.keys(_get(config, [type, 'scorings', 'items'], {})).forEach(
        scoringKey => {
          let value;
          if (scoringKey === 'overallScoring') {
            value = overallScoring;
          } else if (scoringKey.includes('scoreComponent')) {
            const [, index] = scoringKey.split('scoreComponent');
            value = scorings[Number(index) - 1];
          }

          if (
            value &&
            (!_has(config, [type, 'scorings', 'items', scoringKey, 'min']) ||
              value >=
                _get(config, [type, 'scorings', 'items', scoringKey, 'min'])) &&
            (!_has(config, [type, 'scorings', 'items', scoringKey, 'max']) ||
              value <=
                _get(config, [type, 'scorings', 'items', scoringKey, 'max']))
          ) {
            filters.push({
              value: _get(config, [
                type,
                key,
                'items',
                scoringKey,
                'title',
                locale,
              ]),
            });
          }
        },
      );
    } else {
      const value = _get(answers, key, undefined);

      if (
        Array.isArray(value) &&
        _some(value, item =>
          Object.keys(_get(config, [type, key, 'items'], {})).includes(item),
        )
      ) {
        value.forEach(item => {
          if (
            Object.keys(_get(config, [type, key, 'items'], {})).includes(item)
          )
            filters.push({
              label: _get(config, [type, key, 'label', locale]).replace(
                '<value>',
                _get(config, [type, key, 'items', item, locale]),
              ),
              value: _get(config, [type, key, 'items', item, locale]),
            });
        });
      } else if (
        Object.keys(_get(config, [type, key, 'items'], {})).includes(value)
      )
        filters.push({
          label: _get(config, [type, key, 'label', locale]).replace(
            '<value>',
            _get(config, [type, key, 'items', value, locale]),
          ),
          value: _get(config, [type, key, 'items', value, locale]),
        });
    }
  });

  return filters;
};

export const downloadPagePdf = async slug => {
  const node = document.getElementById('assessment-result');
  const options = {
    margin: [0, 0, 0, 0],
    filename: `${slug}.pdf`,
    image: { type: 'jpg', quality: 0.98 },
    html2canvas: {
      dpi: 192,
      scale: 1.8,
      allowTaint: false,
      useCORS: true,
      foreignObjectRendering: false,
      letterRendering: true,
    },
    pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    jsPDF: { unit: 'pt', format: 'letter', orientation: 'portrait' },
  };

  await html2pdf()
    .set(options)
    .from(node)
    .save();
};

export const registerClientResources = ({
  accessCode,
  client,
  firestore,
  ids,
  slugs,
  userId,
  assessmentSlug,
}) => {
  firestore.collection('client_resources_view_log').add({
    event: 'view',
    accessCode,
    assessment: assessmentSlug,
    userId,
    client,
    slugs,
    ids,
    timestamp: Date.now(),
  });
};

export const filterTopicsByExcludedTopics = (topics, excludedTopics = []) => {
  if (!Array.isArray(topics)) {
    return topics;
  }

  if (!excludedTopics || excludedTopics.length === 0) return topics;

  return topics.filter(
    topic => !excludedTopics.includes(_get(topic, 'sys.id')),
  );
};
export const filterResourcesByTypes = ({
  resources,
  types,
  excludedTopicId = [],
}) => {
  if (!resources) return [];
  if (!types) return resources;

  const hasNews = types.includes('news');
  const hasInsights = types.includes('insights');

  return resources.filter(resource => {
    const contentType = _get(resource, 'sys.contentType.sys.id');
    const resourceType = _get(resource, 'fields.type');
    const topics = _get(resource, 'fields.topics', []);
    const hasExcludedTopic = topics.some(topic =>
      excludedTopicId.includes(_get(topic, 'sys.id')),
    );
    if (hasExcludedTopic) return false;
    if (hasNews && resourceType === 'News') return false;
    if (hasInsights && resourceType === 'Blog') return false;

    return !types.includes(contentType);
  });
};
